import * as React from "react";
import { FieldErrorContext } from "react-aria-components";

export type Style = "legacy" | "modern";

interface StyleContextProps {
  style: Style;
  defaultLabelStyle: "default" | "small";
}

const StyleContext = React.createContext<StyleContextProps>({
  style: "modern",
  defaultLabelStyle: "default",
});

export const StyleProvider: React.FC<{
  defaultLabelStyle?: StyleContextProps["defaultLabelStyle"];
  children: React.ReactNode;
}> = ({ defaultLabelStyle = "default", children }) => {
  const value = React.useMemo<StyleContextProps>(
    () => ({ style: "modern", defaultLabelStyle }),
    [defaultLabelStyle],
  );

  return <StyleContext value={value}>{children}</StyleContext>;
};

/**
 * @deprecated We've removed the legacy style, so this hook is no longer needed.
 */
// eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix
export function useStyle(): Style {
  return "modern";
}

export function useDefaultLabelStyle(): StyleContextProps["defaultLabelStyle"] {
  const { defaultLabelStyle } = React.use(StyleContext);
  return defaultLabelStyle;
}

export function useNullableContext<T>(context: React.Context<T>): T | null {
  return React.use(context);
}

export function useFieldErrorContext() {
  // For some reason, `FieldErrorContext` is not nullable out of the box.
  // https://www.runpkg.com/?react-aria-components@1.4.1/src/FieldError.tsx#19
  // https://www.runpkg.com/?react-aria-components@1.4.1/dist/types.d.ts#93
  return useNullableContext(FieldErrorContext);
}
